import { Button, Modal, Select, TextField } from '@restomarker/ui-kit';
import { useEffect, useState, type FormEvent, type ReactElement } from 'react';

import { useBindKiosk, useLocations, useRevokeKiosk } from '@/modules/locations';
import {
  clearKioskBinding,
  loadKioskBinding,
  saveKioskBinding,
  type KioskBinding,
} from '@/shared/auth';
import { HttpError } from '@/shared/api';
import { t } from '@/shared/i18n';

import styles from './bindKioskDialog.module.css';

export interface BindKioskDialogProps {
  onClose: () => void;
  /** Выйти из аккаунта: после выхода закреплённый планшет покажет карточки. */
  onLogout: () => void;
}

/**
 * Закрепление планшета за точкой (T-040, решение Д9).
 *
 * Делает менеджер точки или владелец сети — на месте, у самого планшета, один раз.
 * Ключ приходит в ответе единственный раз и сохраняется в хранилище устройства; больше
 * его не видит никто.
 *
 * Если планшет уже закреплён, диалог это говорит и предлагает открепить, а не закрепить
 * второй раз: второе закрепление оставило бы первое живым ключом, о котором никто
 * не помнит.
 */
export function BindKioskDialog({ onClose, onLogout }: BindKioskDialogProps): ReactElement {
  const locations = useLocations();
  const bind = useBindKiosk();
  const revoke = useRevokeKiosk();

  const [current, setCurrent] = useState<KioskBinding | null | undefined>(undefined);
  const [location, setLocation] = useState('');
  const [name, setName] = useState('');
  const [done, setDone] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Диалог монтируется заново при каждом открытии (см. `KioskNav`), поэтому сброс
  // состояния не нужен — только прочитать, закреплён ли планшет уже.
  useEffect(() => {
    void loadKioskBinding().then(setCurrent);
  }, []);

  const options = (locations.data?.results ?? []).map((item) => ({
    value: item.id,
    label: item.name,
  }));
  const chosen = location || options[0]?.value || '';
  const noLocations = locations.isSuccess && options.length === 0;

  async function handleBind(event: FormEvent): Promise<void> {
    event.preventDefault();
    setError(null);
    try {
      const created = await bind.mutateAsync({ location: chosen, name });
      const binding: KioskBinding = {
        token: created.kiosk_token,
        id: created.id,
        name: created.name,
        locationName: created.location_name,
        locationId: created.location,
      };
      await saveKioskBinding(binding);
      setCurrent(binding);
      setDone(t('kiosk.bind.done').replace('{location}', created.location_name));
    } catch (failure) {
      setError(messageOf(failure));
    }
  }

  async function handleRevoke(): Promise<void> {
    if (!current) return;
    setError(null);
    try {
      await revoke.mutateAsync(current.id);
    } catch (failure) {
      // Уже откреплён на сервере или удалён — локальный ключ всё равно мёртв,
      // и держать его значило бы показывать карточки, которые не впустят.
      if (!(failure instanceof HttpError && failure.status === 404)) {
        setError(messageOf(failure));
        return;
      }
    }
    await clearKioskBinding();
    setCurrent(null);
    setDone(t('kiosk.bind.revokeDone'));
  }

  const footer = current ? (
    <>
      <Button onClick={onClose}>{t('common.close')}</Button>
      {done ? (
        <Button variant="primary" onClick={onLogout}>
          {t('kiosk.bind.logoutNow')}
        </Button>
      ) : (
        <Button variant="secondary" disabled={revoke.isPending} onClick={() => void handleRevoke()}>
          {t('kiosk.bind.revoke')}
        </Button>
      )}
    </>
  ) : (
    <>
      <Button onClick={onClose}>{t('common.cancel')}</Button>
      <Button
        variant="primary"
        type="submit"
        form="bind-kiosk-form"
        disabled={bind.isPending || !chosen || name.trim() === ''}
      >
        {t('kiosk.bind.submit')}
      </Button>
    </>
  );

  return (
    <Modal
      open
      onOpenChange={(value) => (value ? undefined : onClose())}
      title={t('kiosk.bind.title')}
      description={current ? undefined : t('kiosk.bind.description')}
      footer={current === undefined ? undefined : footer}
    >
      {done ? (
        <p className={styles.done} role="status">
          {done}
        </p>
      ) : null}

      {current && !done ? (
        <p className={styles.note}>
          {t('kiosk.bind.already')
            .replace('{location}', current.locationName)
            .replace('{name}', current.name)}
        </p>
      ) : null}

      {current === null && noLocations ? (
        <p className={styles.note}>{t('kiosk.bind.noLocations')}</p>
      ) : null}

      {current === null && !noLocations ? (
        <form id="bind-kiosk-form" className={styles.form} onSubmit={(e) => void handleBind(e)}>
          {options.length > 1 ? (
            <Select
              label={t('kiosk.bind.location')}
              options={options}
              value={chosen}
              onValueChange={(value) => setLocation(value)}
            />
          ) : null}
          <TextField
            label={t('kiosk.bind.name')}
            hint={t('kiosk.bind.nameHint')}
            value={name}
            maxLength={64}
            onChange={(event) => setName(event.target.value)}
          />
        </form>
      ) : null}

      {error ? (
        <p className={styles.error} role="alert">
          {error}
        </p>
      ) : null}
    </Modal>
  );
}

function messageOf(failure: unknown): string {
  if (failure instanceof HttpError) {
    const payload = failure.payload as { error?: { message?: string } } | undefined;
    return payload?.error?.message ?? t('auth.error.generic');
  }
  return t('auth.error.network');
}
